import { FC, ReactElement } from "react"
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material"
import { Userbot } from "../../interfaces"
import { formatPhone } from "./identityFormat"

export interface IdentityTooltipProps {
  children: ReactElement
  account?: Userbot.Account | Userbot.AccountFeedItem | null
  client?: Userbot.Client | Userbot.FeedClient | null
  proxyLabel?: string | null
}

interface RowProps {
  label: string
  value?: React.ReactNode
  mono?: boolean
}

const TooltipRow: FC<RowProps> = ({ label, value, mono }) => {
  if (value === undefined || value === null || value === "") return null
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={2}
      fontSize="0.75rem"
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 90 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 500,
          fontFamily: mono ? "monospace" : "inherit",
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export const IdentityTooltip: FC<IdentityTooltipProps> = ({
  children,
  account,
  client,
  proxyLabel,
}) => {
  if (!account && !client) return children

  const fullClient =
    client && "apiId" in client ? (client as Userbot.Client) : null
  const fullName = account
    ? [account.firstName, account.lastName].filter(Boolean).join(" ")
    : ""
  const formattedPhone = account?.phone ? formatPhone(account.phone) : ""

  const content = (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        bgcolor: "transparent",
        color: "inherit",
        maxWidth: 320,
      }}
    >
      <Stack spacing={0.5}>
        {account && (
          <>
            {client && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "primary.light",
                }}
              >
                Аккаунт
              </Typography>
            )}
            <TooltipRow
              label="TG User ID"
              value={account.telegramUserId}
              mono
            />
            <TooltipRow label="Имя" value={fullName} />
            <TooltipRow
              label="Username"
              value={account.username ? `@${account.username}` : undefined}
            />
            <TooltipRow label="Телефон" value={formattedPhone} mono />
            <TooltipRow label="Комментарий" value={account.comment} />
            <TooltipRow
              label="2FA"
              value={account.hasTwoFaPassword ? "Задан" : "Не задан"}
            />
          </>
        )}

        {client && (
          <>
            {account && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "primary.light",
                  mt: 0.5,
                }}
              >
                Сессия
              </Typography>
            )}
            <TooltipRow label="Сессия" value={client.name} />
            <TooltipRow
              label="Статус"
              value={
                client.isOnline
                  ? "Онлайн"
                  : client.isAuthorized
                    ? "Авторизован (офлайн)"
                    : client.authorizedAt
                      ? "Сессия протухла"
                      : "Ожидает логина"
              }
            />
            <TooltipRow
              label="Онлайн"
              value={
                client.lastOnlineAt
                  ? new Date(client.lastOnlineAt).toLocaleString("ru")
                  : undefined
              }
            />
            <TooltipRow
              label="Использован"
              value={
                client.lastUsedAt
                  ? new Date(client.lastUsedAt).toLocaleString("ru")
                  : undefined
              }
            />
            <TooltipRow
              label="Прокси"
              value={
                proxyLabel ??
                (client.proxyId ? String(client.proxyId) : undefined)
              }
            />
            {fullClient?.deviceModel && (
              <TooltipRow label="Устройство" value={fullClient.deviceModel} />
            )}
            {fullClient?.apiId != null && (
              <TooltipRow
                label="API ID"
                value={String(fullClient.apiId)}
                mono
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  )

  return (
    <Tooltip title={content} arrow enterDelay={400}>
      {children}
    </Tooltip>
  )
}
